import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { onAutocompleteChangeInForm } from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { MaritalStatusType } from 'constants/loanApplication';
import { maritalStatusOptions } from 'constants/options';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getConditionalObject } from 'utils/data';
import { getPersonFullName } from 'utils/loanApplication';

import { getStateSigningRequirements } from 'store/loanApplications/actions';
import {
  selectAlphabeticallySortedCountiesByStates,
  selectIsRefinanceLoanPurposeFromApplication,
  selectIsSpouseSignatureIsRequired,
  selectIsSubjectPropertyPrimaryResidence,
  selectNonBorrowersTableData,
} from 'store/loanApplications/selectors';

import CrmContactSection from '../../../../../CrmContactSection';
import { NEW_ENTITY } from '../../../../EditEntityModal.constants';
import { pagesPropTypes, SPOUSE_TYPE } from '../../MainContent.constants';

import { crmContactSectionStyles, wrapperStyles } from './PersonaInfo.styles';
import { getPersonalInfoConfig } from './PersonalInfo.config';
import { normalizeCurrentResidence, splitConfigAtContactInformation } from './PersonalInfo.utils';

const PersonalInfo = ({
  changedFields,
  isLoading,
  isSaving,
  form,
  validationErrors,
  validateField,
  handleChange,
  entityId,
  section,
  setEntityConfig,
  setInForm,
  setMultipleInForm,
  setForm,
  className,
  entityData: applicationEntityData,
  pendingChanges,
  disabledPendingChangesHighlight,
  applicationData,
  setIsLoading,
}) => {
  const dispatch = useDispatch();

  const isRefinanceFlow = useSelector(selectIsRefinanceLoanPurposeFromApplication);
  const isOccupancyPrimaryResidence = useSelector(selectIsSubjectPropertyPrimaryResidence);
  const isSpouseSignatureIsRequired = useSelector(selectIsSpouseSignatureIsRequired);
  const nonBorrowers = useSelector(selectNonBorrowersTableData);
  const counties = useSelector(selectAlphabeticallySortedCountiesByStates);

  const prevRelationOptions = useRef([]);

  const entityFormData = form[entityId];
  // current entity relationship fields
  const currentSpouseName = entityFormData?.relation?.name;
  const currentSpouseValue = entityFormData?.relation?.value;
  const isCurrentSpouseBorrower = entityFormData?.relation?.type === SPOUSE_TYPE.BORROWER;
  const isCurrentSpouseOther = currentSpouseValue === SPOUSE_TYPE.OTHER;
  const currentSpouseId = isCurrentSpouseOther ? null : entityFormData?.relation?.id;
  // spouse entity in form
  const spouseEntityFormData = form[currentSpouseId];
  const isSpouseEntityExistInForm = !!spouseEntityFormData;
  const spouseEntityName = isSpouseEntityExistInForm ? getPersonFullName(spouseEntityFormData) : '';
  // reset variables
  const isMarried = entityFormData?.maritalStatus?.value === MaritalStatusType.MARRIED;
  const isCurrentSpouseNameDeprecated =
    spouseEntityName && !isCurrentSpouseOther && spouseEntityName !== currentSpouseName;
  const isPrimaryBorrower = applicationEntityData?.isPrimaryBorrower;
  const primaryBorrowerId = _.find(applicationData, borrower => borrower.isPrimaryBorrower)?.id;
  const formPrimaryBorrower = form[primaryBorrowerId];
  const { currentResidence } = (formPrimaryBorrower || applicationData[primaryBorrowerId]) ?? {};

  const getBorrowerRelationOptions = useCallback(
    id => {
      const entityFormData = form[id];
      const entityApplicationData = applicationData[id];

      const isNewBorrower = id === NEW_ENTITY;
      const name = isNewBorrower
        ? entityApplicationData?.fullName
        : getPersonFullName(entityFormData ?? entityApplicationData);
      const order = isNewBorrower ? _.size(applicationData) : entityApplicationData?.order;
      const value = `${SPOUSE_TYPE.BORROWER}${order}`;
      const type = SPOUSE_TYPE.BORROWER;

      return {
        name,
        value,
        id,
        type,
      };
    },
    [applicationData, form],
  );

  const {
    name: currentEntityFullName,
    value: currentEntitySpouseValue,
    type: currentEntitySpouseType,
  } = useMemo(() => getBorrowerRelationOptions(entityId), [entityId, getBorrowerRelationOptions]);

  const currentEntityRelationOption = useMemo(
    () => ({
      name: currentEntityFullName,
      value: currentEntitySpouseValue,
      id: entityId,
      type: currentEntitySpouseType,
    }),
    [currentEntityFullName, currentEntitySpouseValue, currentEntitySpouseType, entityId],
  );

  const otherBorrowersMap = useMemo(
    () => _.omit({ ...applicationData, ...form }, entityId),
    [applicationData, form, entityId],
  );

  const nbeOptions = useMemo(
    () =>
      (nonBorrowers ?? []).reduce((acc, nonBorrower) => {
        const spouseType = nonBorrower.spouse?.type;

        if (spouseType && !spouseType.startsWith(SPOUSE_TYPE.BORROWER)) {
          return acc;
        }

        const nonBorrowerValueAsSpouse = `${SPOUSE_TYPE.NON_BORROWER}${nonBorrower.orderNumber}`;

        const isCurrentNbeSpouseOfAnotherBorrower = Object.values(otherBorrowersMap).some(
          ({ relation }) =>
            relation === nonBorrowerValueAsSpouse || relation?.value === nonBorrowerValueAsSpouse,
        );

        if (isCurrentNbeSpouseOfAnotherBorrower) {
          return acc;
        }

        const option = {
          name: nonBorrower.fullName,
          value: `${SPOUSE_TYPE.NON_BORROWER}${nonBorrower.orderNumber}`,
          id: nonBorrower.id,
          type: SPOUSE_TYPE.NON_BORROWER,
        };

        acc.push(option);

        return acc;
      }, []),
    [nonBorrowers, otherBorrowersMap],
  );

  const relationOptions = useMemo(() => {
    const borrowerOptions = Object.entries(otherBorrowersMap).reduce((acc, [id, borrower]) => {
      const isMarried =
        borrower.maritalStatus === MaritalStatusType.MARRIED ||
        borrower.maritalStatus?.value === MaritalStatusType.MARRIED;
      const hasRelationship = !!borrower.relation;
      const isSpouseOfCurrentBorrower =
        hasRelationship &&
        (borrower.relation === currentEntitySpouseValue ||
          borrower.relation?.value === currentEntitySpouseValue);

      if (isMarried && hasRelationship && !isSpouseOfCurrentBorrower) {
        return acc;
      }

      acc.push(getBorrowerRelationOptions(id));

      return acc;
    }, []);

    const mergedOptions = [
      ...borrowerOptions,
      ...nbeOptions,
      {
        name: 'Other',
        value: SPOUSE_TYPE.OTHER,
      },
    ];

    const isEqual = _.isEqual(mergedOptions, prevRelationOptions.current);

    if (!isEqual) {
      prevRelationOptions.current = mergedOptions;

      return mergedOptions;
    }

    return prevRelationOptions.current;
  }, [currentEntitySpouseValue, nbeOptions, getBorrowerRelationOptions, otherBorrowersMap]);

  const customOnChangeDependents = useCallback(
    (e, value) => {
      const target = e.currentTarget.querySelector('[data-fieldname]') || e.currentTarget;

      if (target) {
        const { fieldname } = target.dataset;
        const { name } = target;
        const { fieldName, fieldValue } = { fieldName: fieldname || name, fieldValue: value || '' };

        setForm(form => {
          const formattedForm = { ...form };

          const count = fieldValue.value;
          const { agesOfDependents } = formattedForm[entityId].dependents ?? {};

          formattedForm[entityId].dependents = {
            count: value,
            ...getConditionalObject(count, () => ({
              agesOfDependents: Object.fromEntries(
                Object.entries(agesOfDependents ?? []).filter((_, index) => index <= value),
              ),
            })),
          };

          return formattedForm;
        });

        setInForm(fieldName, fieldValue);

        if (validateField) {
          setTimeout(() => validateField(fieldName, fieldValue));
        }
      }
    },
    [setForm, setInForm, validateField, entityId],
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    dispatch(getStateSigningRequirements()).finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading]);

  useEffect(() => {
    if (!isCurrentSpouseNameDeprecated) {
      return;
    }

    // * need if we edited spouse name in current edit session
    setInForm(`[${entityId}].relation.name`, spouseEntityName, false);
  }, [spouseEntityName, entityId, isCurrentSpouseNameDeprecated, setInForm]);

  useEffect(() => {
    if (!currentSpouseId || !isCurrentSpouseBorrower) {
      return;
    }

    const spousePersonConfig = getPersonalInfoConfig({
      entityId: currentSpouseId,
      section,
      relationOptions: [currentEntityRelationOption],
      isRelationshipFieldsShown: true,
      isConfigForSpouseEntity: true,
    });

    // * we need to set spouse entity in config in case when we didn't open spouse entity tab
    setEntityConfig(spousePersonConfig);
  }, [
    currentEntityRelationOption,
    isSpouseEntityExistInForm,
    isCurrentSpouseBorrower,
    section,
    setEntityConfig,
    currentSpouseId,
  ]);

  useEffect(() => {
    if (!currentSpouseId || !isCurrentSpouseBorrower || !isSpouseEntityExistInForm) {
      return;
    }

    setMultipleInForm(
      {
        [`[${currentSpouseId}].maritalStatus`]: maritalStatusOptions[0],
        [`[${currentSpouseId}].relation`]: currentEntityRelationOption,
      },
      false,
    );
  }, [
    currentEntityRelationOption,
    isSpouseEntityExistInForm,
    isCurrentSpouseBorrower,
    setMultipleInForm,
    currentSpouseId,
  ]);

  const onRelationInputChange = useCallback(
    (e, option) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      if (currentSpouseId && isCurrentSpouseBorrower) {
        setMultipleInForm(
          {
            [`[${currentSpouseId}].maritalStatus`]: '',
            [`[${currentSpouseId}].relation`]: '',
          },
          false,
        );
      }

      if (isCurrentSpouseOther) {
        setMultipleInForm(
          {
            [`[${entityId}].spouseFirstName`]: '',
            [`[${entityId}].spouseLastName`]: '',
            [`[${entityId}].spouseEmail`]: '',
          },
          false,
        );
      }
    },
    [
      entityId,
      isCurrentSpouseOther,
      isCurrentSpouseBorrower,
      setInForm,
      setMultipleInForm,
      currentSpouseId,
      validateField,
    ],
  );

  const onMaritalStatusInputChange = useCallback(
    (e, option) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      if (option?.value !== MaritalStatusType.MARRIED && currentSpouseId) {
        setInForm(`[${entityId}].relation`, '', false);

        if (isCurrentSpouseOther) {
          setMultipleInForm(
            {
              [`[${entityId}].spouseFirstName`]: '',
              [`[${entityId}].spouseLastName`]: '',
              [`[${entityId}].spouseEmail`]: '',
            },
            false,
          );
        }

        if (isCurrentSpouseBorrower) {
          setMultipleInForm(
            {
              [`[${currentSpouseId}].maritalStatus`]: '',
              [`[${currentSpouseId}].relation`]: '',
            },
            false,
          );
        }
      }
    },
    [
      entityId,
      isCurrentSpouseOther,
      isCurrentSpouseBorrower,
      setInForm,
      setMultipleInForm,
      currentSpouseId,
      validateField,
    ],
  );

  const onIsLiveTogetherForTwoYearsChange = useCallback(
    (e, value) => {
      const { name } = e.target;

      if (value) {
        setForm(prevForm => ({
          ...prevForm,
          [entityId]: {
            ...prevForm[entityId],
            currentResidence: formPrimaryBorrower
              ? currentResidence
              : normalizeCurrentResidence(currentResidence, counties),
          },
        }));
      }

      setInForm(name, value);
      validateField(name, value);
    },
    [currentResidence, entityId, formPrimaryBorrower, setForm, setInForm, validateField, counties],
  );

  const config = useMemo(
    () =>
      getPersonalInfoConfig({
        entityId,
        numberOfDependents: entityFormData?.dependents?.count?.value,
        isPrimaryBorrower,
        section,
        customOnChangeDependents,
        isRelationshipFieldsShown:
          isRefinanceFlow &&
          isOccupancyPrimaryResidence &&
          isSpouseSignatureIsRequired &&
          isMarried,
        isCurrentSpouseOther,
        relationOptions,
        onRelationInputChange,
        onMaritalStatusInputChange,
        onIsLiveTogetherForTwoYearsChange,
      }),
    [
      entityId,
      entityFormData?.dependents?.count?.value,
      isPrimaryBorrower,
      section,
      customOnChangeDependents,
      onMaritalStatusInputChange,
      isRefinanceFlow,
      isOccupancyPrimaryResidence,
      isSpouseSignatureIsRequired,
      isCurrentSpouseOther,
      relationOptions,
      isMarried,
      onRelationInputChange,
      onIsLiveTogetherForTwoYearsChange,
    ],
  );

  const { upperConfig, lowerConfig } = useMemo(
    () => splitConfigAtContactInformation(config),
    [config],
  );

  useEffect(() => {
    setEntityConfig(config);
  }, [config, setEntityConfig]);

  if (!config) {
    return null;
  }

  return (
    <Box className={className} sx={wrapperStyles}>
      <FormGenerator
        ignoreEmptyGroups
        isNestedForm
        changedFields={changedFields}
        config={upperConfig}
        disabledPendingChangesHighlight={disabledPendingChangesHighlight}
        form={form}
        formName={FormNames.BORROWER_INFO}
        groupGap={24}
        handleChange={handleChange}
        isLoading={isSaving || isLoading}
        numberOfColumns={4}
        pendingChanges={pendingChanges}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />

      <Box sx={crmContactSectionStyles}>
        <CrmContactSection
          email={entityFormData?.email}
          entityId={entityId}
          firstName={entityFormData?.firstName}
          lastName={entityFormData?.lastName}
          phone={entityFormData?.cellPhone}
        />
      </Box>

      {lowerConfig && (
        <FormGenerator
          ignoreEmptyGroups
          isNestedForm
          changedFields={changedFields}
          config={lowerConfig}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.BORROWER_INFO}
          groupGap={24}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      )}
    </Box>
  );
};

PersonalInfo.propTypes = pagesPropTypes;

export default memo(PersonalInfo);
